import { DomesticCartBenefitBanner } from '~/api/converters/domestic/cart/BenefitBanner/index.type'
import { DomesticCartProductSection } from '~/api/converters/domestic/cart/ProductSection/index.type'
import { TCommonViewer } from '~/api/routes/common/type'
import { TMaxDiscountLimit } from '~/api/routes/domestic/order/controller/type'
import { ICouponInstances } from '~/api/services/coupon-platform/index.type'
import {
  TDomesticCouponCheckProgressOfPropertyResponseData,
  TDomesticCouponResponseData,
  TDomesticCouponsOfPropertyResponseData,
} from '~/api/services/coupon/index.type'
import {
  TDomesticPropertyResponseType,
  TDomesticRoomTypeResponseType,
} from '~/api/services/domestic-property/index.type'
import { BenefitBanner } from '~/api/services/joy-api/index.type'
import {
  ILeisureWebApiProduct,
  ILeisureWebApiVariant,
} from '~/api/services/leisure-web-api/index.type'
import {
  IGetPropertyConfigResponse,
  TGetRatePlanConfigResponse,
} from '~/api/services/product-config/index.type'
import { TRatePlanByIdResponseData } from '~/api/services/rate-plan-api/index.type'
import {
  DomesticAccommodationCartItem,
  DomesticAccommodationCartItemOption,
  DomesticBusCartItem,
  DomesticBusCartItemOption,
  DomesticLeisureCartItem,
  DomesticLeisureCartItemOption,
  DomesticTrainCartItem,
  DomesticTrainCartItemOption,
} from '~/api/services/shopping-cart-api/index.type'

export const DomesticCartAlertAction = {
  NONE: 'NONE',
  DISMISS: 'DISMISS',
  LOGIN: 'LOGIN',
  REFRESH: 'REFRESH',
  HOME: 'HOME',
} as const

/** ================================================ */

/** 장바구니 Option > 국내숙소 */
export type DomesticPropertyCartItemOptionMashup = {
  roomType: TDomesticRoomTypeResponseType['data'] & {
    version: TDomesticRoomTypeResponseType['version']
  }
  coupons?: TDomesticCouponResponseData
  additionalOneCoupons?: TDomesticCouponResponseData
  additionalTwoCoupons?: TDomesticCouponResponseData
  propertyCoupons: TDomesticCouponsOfPropertyResponseData
  propertyCouponsProgress: TDomesticCouponCheckProgressOfPropertyResponseData
  ratePlan: TRatePlanByIdResponseData | null
  ratePlanConfig: TGetRatePlanConfigResponse | null
  propertyConfig: IGetPropertyConfigResponse
} & DomesticAccommodationCartItemOption

/** 장바구니 Option > 국내레저 */
export type DomesticLeisureCartItemOptionMashup = {
  product: ILeisureWebApiProduct
  variant: ILeisureWebApiVariant
  coupons: ICouponInstances[]
} & DomesticLeisureCartItemOption

/** 장바구니 Option > 기차 */
export type DomesticTrainCartItemOptionMashup = {
  coupons: ICouponInstances[]
} & DomesticTrainCartItemOption

/** 장바구니 Option > 버스 */
export type DomesticBusCartItemOptionMashup = {
  coupons: ICouponInstances[]
} & DomesticBusCartItemOption

/** ================================================ */

/** 장바구니 Item > 국내숙소 */
export type CartPropertyItemMashup = Omit<DomesticAccommodationCartItem, 'options'> & {
  property: TDomesticPropertyResponseType['data'] & { published: boolean }
  options: DomesticPropertyCartItemOptionMashup[]
}

/** 장바구니 Item > 국내레저 */
export type CartLeisureItemMashup = Omit<DomesticLeisureCartItem, 'options'> & {
  options: DomesticLeisureCartItemOptionMashup[]
}

/** 장바구니 Item > 기차 */
export type CartTrainItemMashup = Omit<DomesticTrainCartItem, 'options'> & {
  options: DomesticTrainCartItemOptionMashup[]
}

/** 장바구니 Item > 버스 */
export type CartBusItemMashup = Omit<DomesticBusCartItem, 'options'> & {
  options: DomesticBusCartItemOptionMashup[]
}

/** 장바구니 Items 전체 배열  */
export type CartItemsMashup = Array<
  CartPropertyItemMashup | CartLeisureItemMashup | CartTrainItemMashup | CartBusItemMashup
>

/** ================================================ */

/** 장바구니 converter params */
export type DomesticCartConverterParams = {
  isMember: boolean
  viewer: TCommonViewer
  cartItemsMashup: CartItemsMashup
  benefitBanner: BenefitBanner
  maxDiscountLimit: TMaxDiscountLimit
}

/** 장바구니 converter response */
export type DomesticCartConverterResponse = {
  isMember: boolean
  viewer: TCommonViewer
  productSection: DomesticCartProductSection
  maxDiscountLimit: TMaxDiscountLimit
  benefitBanner: DomesticCartBenefitBanner
}

/** ================================================ */

/** 장바구니 대실 시간 정보 조회 converter params */
export type DomesticCartDayuseTimeLinesConverterParams = {
  /** HH:mm */
  checkInTime: string
  checkOutTime: string
  /** 입실 가능한 대실의 마지막 시간 */
  lastCheckInTime: string
  /** 대실 이용 시간 */
  useTime: number
  /** yyyy-MM-dd */
  checkInDate: string
}
/** 장바구니 대실 시간 정보 조회 converter response */
export interface DomesticCartDayuseTimeLinesConverterResponse {
  timeLines: DomesticCartDayuseTimeLine[]
  maxUseTime: number
  lastCheckInTime: string
}

export type DomesticCartDayuseTimeLine = {
  /** 대실 시간 범위 텍스트 (ex. 10:00 - 12:00) */
  timeRangeText: string
  /** 대실 체크인/아웃 시간 (yyyy-MM-dd HH:mm) */
  checkInAt: string
  checkOutAt: string
}

/** ================================================ */

/** 장바구니 구매하기 response */
export type DomesticCartPurchaseResponse = {
  success: true
  querystring: string
  discountPriceByMemberClass: number
}
